import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FileUpload } from '../components';
import SidebarDocumentsList from '../components/documents/SidebarDocumentsList';
import DocumentsEmptyState from '../components/documents/DocumentsEmptyState';
import CenteredModal from '../components/ui/CenteredModal';
import DocumentView from './DocumentView';
import TutorialVideoButton from '../components/common/TutorialVideoButton';
import './DocumentsPage.css';

function DocumentsPage() {
  const [refreshKey, setRefreshKey] = useState(0);
  const [showUpload, setShowUpload] = useState(false);
  const [docCount, setDocCount] = useState(null);
  // workspaceMode: "browse" = docs panel + launcher strip, "focus" = notebook panel visible
  const [workspaceMode, setWorkspaceMode] = useState('browse');
  const [focusTabIndex, setFocusTabIndex] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();

  const selectedDocId = useMemo(() => {
    const doc = searchParams.get('doc');
    return doc ? String(doc) : null;
  }, [searchParams]);

  const hasSelectedDoc = Boolean(selectedDocId);

  // When no document is selected, always reset to browse
  useEffect(() => {
    if (!hasSelectedDoc) setWorkspaceMode('browse');
  }, [hasSelectedDoc]);

  const setSelectedDocId = useCallback((docId) => {
    const next = new URLSearchParams(searchParams);
    if (docId == null || docId === '') next.delete('doc');
    else next.set('doc', String(docId));
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const switchToBrowse = useCallback(() => setWorkspaceMode('browse'), []);
  const switchToFocus = useCallback((tabIndex = 0) => {
    setFocusTabIndex(tabIndex);
    setWorkspaceMode('focus');
  }, []);

  // Layout class drives the CSS grid
  const layoutClass = [
    'docsws',
    hasSelectedDoc ? 'has-doc' : '',
    hasSelectedDoc ? `mode-${workspaceMode}` : '',
  ].filter(Boolean).join(' ');

  return (
    <div className="page documents-workspace-page">
      <div className="page-inner docsws-inner">
        <div className={layoutClass}>

          {/* ─── Documents Panel: full document list (browse mode only) ─── */}
          {(workspaceMode === 'browse' || !hasSelectedDoc) && (
            <aside className={hasSelectedDoc ? 'docsws-documents-panel' : 'docsws-sidebar'} aria-label="Documents">
              <div className="docsws-sidebar-header">
                <div className="docsws-sidebar-title">
                  <svg className="documents-header-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" fill="var(--color-brand-primary, #0560fd)"/>
                    <path d="M14 2v6h6" fill="var(--color-text-primary, #e2e8f0)"/>
                    <line x1="16" y1="13" x2="8" y2="13" stroke="var(--color-text-primary, #e2e8f0)" strokeWidth="1.5" strokeLinecap="round"/>
                    <line x1="16" y1="17" x2="8" y2="17" stroke="var(--color-text-primary, #e2e8f0)" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                  Documents
                </div>
                <div className="docsws-sidebar-actions">
                  <button
                    type="button"
                    className="docsws-upload-btn"
                    onClick={() => setShowUpload(true)}
                    title="Upload files"
                    aria-label="Upload files"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="17 8 12 3 7 8" />
                      <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                    Upload
                  </button>
                </div>
              </div>
              <div className="docsws-list">
                <SidebarDocumentsList
                  key={refreshKey}
                  selectedDocumentId={selectedDocId}
                  onSelectDocument={(docId) => setSelectedDocId(docId)}
                  onTotalChange={setDocCount}
                  onUploadClick={() => setShowUpload(true)}
                />
              </div>
            </aside>
          )}

          {/* ─── Document Viewer + Notebook Panel ─── */}
          {selectedDocId ? (
            <DocumentView
              documentIdOverride={selectedDocId}
              embedded
              workspaceMode={workspaceMode}
              onSwitchToBrowse={switchToBrowse}
              onSwitchToFocus={switchToFocus}
              focusTabIndex={focusTabIndex}
            />
          ) : (
            <section className="docsws-content" aria-label="Document workspace">
              <DocumentsEmptyState
                hasDocuments={docCount > 0}
                onUploadClick={() => { setShowUpload(true); }}
              />
            </section>
          )}

          {/* ─── Notebook Launcher Strip (browse mode, doc selected) ─── */}
          {hasSelectedDoc && workspaceMode === 'browse' && (
            <div className="docsws-launcher-strip">
              <button type="button" className="docsws-launcher-strip__layout" onClick={() => switchToFocus(0)} title="Open workspace panel" aria-label="Open workspace panel">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <rect x="1" y="1" width="16" height="16" rx="2.5" stroke="currentColor" strokeWidth="1.5" fill="none" />
                  <rect x="10.5" y="1" width="6.5" height="16" rx="2.5" fill="currentColor" opacity="0.5" />
                  <line x1="10.5" y1="1" x2="10.5" y2="17" stroke="currentColor" strokeWidth="1" opacity="0.35" />
                </svg>
              </button>
              <div className="docsws-launcher-strip__divider" />
              <button type="button" className="docsws-launcher-strip__tool" onClick={() => switchToFocus(0)} data-tooltip="Take notes while reading">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
                <span>Notes</span>
              </button>
              <button type="button" className="docsws-launcher-strip__tool" onClick={() => switchToFocus(1)} data-tooltip="Chat with your document">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                <span>Chat</span>
              </button>
              <button type="button" className="docsws-launcher-strip__tool" onClick={() => switchToFocus(2)} data-tooltip="Quizzes, flashcards & podcasts">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>
                <span>Study</span>
              </button>
              <button type="button" className="docsws-launcher-strip__tool" onClick={() => switchToFocus(3)} data-tooltip="View document metadata">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
                <span>Metadata</span>
              </button>
            </div>
          )}
        </div>

        <CenteredModal
          isOpen={showUpload}
          onClose={() => setShowUpload(false)}
          title="Upload materials"
          subtitle="Drop in class files to unlock chat, notes, quizzes, and study tools."
          className="upload-materials-modal"
        >
          <div className="docsws-uploadModalBody">
            <FileUpload
              scoutIQMode
              showCollapseToggle={false}
              onUploadComplete={() => {
                setRefreshKey(k => k + 1);
                setShowUpload(false);
              }}
              onUploadError={() => {}}
            />
          </div>
        </CenteredModal>

        <TutorialVideoButton
          videoUrl="https://www.youtube.com/embed/RwHU7EqjkXI?autoplay=1"
          label="Documents Tutorial"
        />
      </div>
    </div>
  );
}

export default DocumentsPage;
